import React, { FC } from 'react';
import { Box, useTheme, alpha } from '@mui/material';

interface SidebarDelimiterProps {
    onResizeStart?: (e: React.MouseEvent) => void;
    isResizing?: boolean;
    orientation?: 'vertical' | 'horizontal';
    onTogglePosition?: () => void
}

const Icon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg
      width="14"
      height="11"
      viewBox="0 0 14 11"
      fill="none"
      {...props}
    >
      <path
        d="M2.10588 2.15149L8.21317 2.15149C8.39053 2.15149 8.53907 2.21135 8.65879 2.33107C8.77838 2.45065 8.83817 2.59913 8.83817 2.77649C8.83817 2.95385 8.77838 3.10232 8.65879 3.2219C8.53907 3.34163 8.39053 3.40149 8.21317 3.40149L2.10588 3.40149L3.18275 4.47836C3.30664 4.60239 3.36782 4.74746 3.36629 4.91357C3.36463 5.07968 3.30345 5.22739 3.18275 5.35669C3.05345 5.48586 2.90497 5.5526 2.73734 5.5569C2.56956 5.56121 2.42102 5.49871 2.29171 5.3694L0.226086 3.30378C0.0753907 3.15308 4.37389e-05 2.97732 4.37477e-05 2.77649C4.37564e-05 2.57565 0.0753907 2.39989 0.226086 2.24919L2.29171 0.183569C2.41574 0.0596802 2.56289 -0.00150034 2.73317 2.74428e-05C2.90358 0.00169412 3.05345 0.0671108 3.18275 0.196277C3.30345 0.325583 3.36595 0.471972 3.37025 0.635444C3.37456 0.798917 3.31206 0.945305 3.18275 1.07461L2.10588 2.15149ZM11.484 7.15149L10.3815 6.04899C10.2661 5.93357 10.2071 5.7885 10.2044 5.61378C10.2018 5.4392 10.2608 5.29149 10.3815 5.17065C10.5022 5.04996 10.6486 4.98961 10.8207 4.98961C10.9926 4.98961 11.1389 5.04996 11.2596 5.17065L13.3382 7.2492C13.4889 7.39989 13.5642 7.57565 13.5642 7.77649C13.5642 7.97732 13.4889 8.15308 13.3382 8.30378L11.2596 10.3823C11.1442 10.4976 10.9992 10.5566 10.8246 10.5594C10.6499 10.562 10.5022 10.503 10.3815 10.3823C10.2608 10.2615 10.2005 10.1151 10.2005 9.94315C10.2005 9.77121 10.2608 9.62482 10.3815 9.50399L11.484 8.40149L5.96963 8.40149C5.79227 8.40149 5.64379 8.34162 5.52421 8.2219C5.40449 8.10232 5.34463 7.95385 5.34463 7.77649C5.34463 7.59913 5.40449 7.45065 5.52421 7.33107C5.64379 7.21135 5.79227 7.15149 5.96963 7.15149L11.484 7.15149Z"
        fill="currentColor"
      />
    </svg>
  );
};

const SidebarDelimiter: FC<SidebarDelimiterProps> = ({
    onResizeStart,
    isResizing = false,
    orientation = 'vertical',
    onTogglePosition,
}) => {
    const theme = useTheme();

    const isVertical = orientation === 'vertical';

    const onChangePosition = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if(onTogglePosition){
            onTogglePosition();
        }
    }

    const stopPropagation = (e: React.FormEvent) => {
        if(onTogglePosition){
            onTogglePosition();
        }
    }

    return (
        <Box
            onMouseDown={onResizeStart}
            sx={{
                display: 'flex',
                flexDirection: isVertical ? 'column' : 'row',
                justifyContent: 'center',
                alignItems: 'center',
                flexShrink: 0,
                position: 'relative',
                ...(isVertical ? {
                    width: '6px',
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                } : {
                    height: '6px',
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }),
                bgcolor: isResizing
                    ? theme.palette.primary.main
                    : theme.palette.secondary.main,
                cursor: isVertical ? 'col-resize' : 'row-resize',
                userSelect: 'none',
                zIndex: 10,
                transition: 'background-color 200ms ease-in-out',
                '&:hover': {
                    bgcolor: isResizing
                        ? theme.palette.primary.main
                        : theme.palette.action.hover || alpha(theme.palette.secondary.main, 0.8),
                    '& > *': {
                        opacity: 1,
                    },
                },
            }}
        >
            <Box
                sx={{
                    ...(isVertical ? {
                        width: '0.125rem',
                        height: '6.25rem',
                    } : {
                        width: '6.25rem',
                        height: '0.125rem',
                    }),
                    borderRadius: '0.3125rem',
                    bgcolor: theme.palette.text.secondary,
                    opacity: 0.6,
                }}
            />
            {!!onTogglePosition && (
                <Box
                    role="button"
                    tabIndex={0}
                    aria-label='Toggle panel position'
                    onClick={onChangePosition}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={stopPropagation}
                    onKeyUp={stopPropagation}
                    sx={{
                        position: 'absolute',
                        top: '33px',
                        left: '50%',
                        transform: 'translate(-50%, -50%) scale(0.9)',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: isResizing
                            ? theme.palette.primary.main
                            : theme.palette.background.paper,
                        border: `1.5px solid ${isResizing ? theme.palette.primary.main : theme.palette.divider}`,
                        boxShadow: isResizing
                            ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                            : theme.shadows[2],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: "pointer",
                        transition: 'opacity 150ms ease-in-out, transform 150ms ease-in-out, background-color 150ms ease-in-out, box-shadow 150ms ease-in-out',
                        zIndex: 11,
                    }}
                >
                    <Icon color={isResizing ? theme.palette.primary.contrastText : theme.palette.text.secondary} />
                </Box>
            )}
        </Box>
    );
};

export default SidebarDelimiter;


